import { useState, useEffect, useRef } from 'react';
import api from '../api';

const PLACEHOLDERS = [
  ['{{audit_name}}',            'Naam van de audit'],
  ['{{client_name}}',           'Naam van de klant'],
  ['{{framework_name}}',        'Framework (bijv. ISO 27001:2022)'],
  ['{{auditor_name}}',          'Naam van de auditor'],
  ['{{audit_date}}',            'Auditdatum'],
  ['{{generated_date}}',        'Datum van rapportgeneratie'],
  ['{{status}}',                'Status van de audit'],
  ['{{scope}}',                 'Toepassingsgebied'],
  ['{{audit_description}}',     'Omschrijving auditronde'],
  ['{{auditees}}',              'Auditees (als ze overal gelijk zijn)'],
  ['{{total}}',                 'Totaal aantal controls'],
  ['{{conform_count}}',         'Aantal conform'],
  ['{{niet_conform_count}}',    'Aantal niet conform'],
  ['{{observatie_count}}',      'Aantal observaties'],
  ['{{kans_verbetering_count}}','Aantal kansen voor verbetering'],
  ['{{nvt_count}}',             'Aantal N.v.t.'],
  ['{{niet_beoordeeld_count}}', 'Aantal niet beoordeeld'],
  ['{{#has_maturity}}', 'Voorwaarde: maturiteitsscores aanwezig (ISO 27001)'],
  ['{{#maturity_themes}}', 'Per Annex A-thema: section, section_title, avg, scored'],
];

const FINDING_PLACEHOLDERS = [
  ['{{control_id}}',    'Control-nummer (bijv. A.5.1)'],
  ['{{title}}',         'Titel van de control'],
  ['{{section}}',       'Sectie (bijv. A.5)'],
  ['{{status_label}}',  'Status in tekst (bijv. Conform)'],
  ['{{situation}}',     'Huidige situatie'],
  ['{{finding}}',       'Bevinding'],
  ['{{recommendation}}','Aanbeveling'],
  ['{{priority}}',      'Prioriteit'],
  ['{{due_date}}',      'Streefdatum'],
  ['{{auditees}}',      'Auditees (per control, als ze verschillen)'],
  ['{{maturity}}',      'Maturiteit (ISO 27001 Annex A)'],
  ['{{notes}}',         'Notities'],
  ['{{#has_details}}',  'Voorwaarde: de control heeft tekst om uit te schrijven'],
];

// Auditplan-sjabloon (zie backend/generateAuditPlan.js → buildPlanTemplateData)
const PLAN_PLACEHOLDERS = [
  ['{{audit_name}} / {{client_name}}', 'Audit en klant'],
  ['{{framework_name}}',  'Framework'],
  ['{{auditor_name}}',    'Auditor'],
  ['{{audit_dates}}',     'Eerste – laatste auditdag'],
  ['{{day_count}}',       'Aantal dagen (bv. 2 dagen)'],
  ['{{audit_type}}',      'Audittype'],
  ['{{location}}',        'Auditlocatie'],
  ['{{contact_person}}',  'Contactpersoon'],
  ['{{exec_onsite}} {{exec_hybrid}} {{exec_remote}}', '☒ / ☐ per uitvoeringsvorm'],
  ['{{criteria}} {{objectives}} {{method}} {{documents}} {{focus}}', 'Tekst (meerdere regels)'],
  ['{{#criteria_list}}{{item}}{{/criteria_list}}', 'Zelfde, als opsomming (ook _list bij de andere)'],
  ['{{coverage_covered}} / {{coverage_total}}', 'Dekking: ingepland / in scope'],
];
const PLAN_LOOPS = [
  ['{{#overview}}', 'Overzicht dagdelen: part, day, theme, session, time'],
  ['{{#days}}', 'Per dag: day_number, day_label, day_short, location, en lus items'],
  ['{{#items}}', 'Per agendapunt: time, title, session_label, code, theme, part_header, topics, #topics_list, clauses, annex, related, attention, auditees, auditee_note, location'],
  ['{{#is_session}} {{#is_break}} {{#is_meeting}}', 'Voorwaarde op het soort agendapunt'],
  ['{{#sessions}}', 'Alle sessies (zelfde velden als items, plus day_label)'],
  ['{{#other_items}}', 'Overige punten (bv. rapportage), zelfde velden'],
  ['{{#coverage}}', 'Dekkingsmatrix: control_id, title, sessions'],
];

const KIND_LABELS = { report: 'Auditrapport', plan: 'Auditplan' };

// Downloaden via de API (blob), zodat de JWT-header meegaat
async function downloadFile(url, filename) {
  const res = await api.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

function PlaceholderTable({ rows }) {
  return (
    <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
      <tbody>
        {rows.map(([ph, desc]) => (
          <tr key={ph}>
            <td className="font-monospace text-primary py-1 px-2" style={{ whiteSpace: 'nowrap' }}>{ph}</td>
            <td className="text-muted py-1 px-2">{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const KINDS = ['report', 'plan'];
const EXAMPLE_NAMES = { report: 'auditrapport-sjabloon', plan: 'auditplan-sjabloon' };
const fwName = (fw) => `${fw.name}${fw.version ? ` (${fw.version})` : ''}`;

export default function TemplatesPage() {
  const [templates,   setTemplates]   = useState([]);
  const [frameworks,  setFrameworks]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [upload,      setUpload]      = useState(null);   // { fw, kind } van het uploadvenster
  const [uploading,   setUploading]   = useState(false);
  const [error,       setError]       = useState('');
  const [form,        setForm]        = useState({ name: '', activate: true });
  const fileRef = useRef(null);

  function load() {
    Promise.all([api.get('/api/templates'), api.get('/api/frameworks')])
      .then(([t, f]) => { setTemplates(t.data); setFrameworks(f.data); })
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function openUpload(fw, kind) {
    setUpload({ fw, kind });
    setForm({ name: '', activate: true });
    setError('');
  }

  async function handleUpload(e) {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) { setError('Selecteer een .docx bestand'); return; }
    if (!form.name) { setError('Geef een naam op'); return; }

    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', form.name);
    fd.append('framework_id', upload.fw.id);
    fd.append('kind', upload.kind);
    fd.append('activate', form.activate ? '1' : '0');

    try {
      await api.post('/api/templates', fd);
      setUpload(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Fout bij uploaden');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(t) {
    const extra = t.is_active ? '\nDit is het actieve sjabloon: daarna geldt de ingebouwde lay-out.' : '';
    if (!window.confirm(`Template "${t.name}" verwijderen?${extra}`)) return;
    await api.delete(`/api/templates/${t.id}`);
    load();
  }

  async function setActive(t, active) {
    try {
      await api.put(`/api/templates/${t.id}/active`, { active });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Fout bij activeren');
    }
  }

  function handleDownload(t) {
    downloadFile(`/api/templates/${t.id}/download`, t.original_filename).catch(() => alert('Fout bij downloaden'));
  }

  function handleExample(kind, fw) {
    const q = fw ? `?framework_id=${fw.id}` : '';
    const name = `${EXAMPLE_NAMES[kind]}${fw ? '-' + fw.code : ''}.docx`;
    downloadFile(`/api/templates/example/${kind}${q}`, name).catch(() => alert('Fout bij downloaden'));
  }

  const unlinked = templates.filter(t => !t.framework_id);

  return (
    <>
      <div className="page-header">
        <h2>Templates</h2>
      </div>

      <div className="page-body">
        {upload && (
          <UploadModal
            upload={upload} form={form} setForm={setForm} fileRef={fileRef}
            error={error} uploading={uploading}
            onSubmit={handleUpload} onClose={() => setUpload(null)}
            onExample={() => handleExample(upload.kind, upload.fw)}
          />
        )}

        <div className="alert alert-info d-flex gap-2 mb-4 py-2">
          <i className="bi bi-info-circle mt-1" />
          <div className="small">
            Per framework en per soort document is één sjabloon <strong>actief</strong>; dat wordt gebruikt bij het genereren.
            Zonder actief sjabloon gebruikt de app de ingebouwde lay-out.
            Start van het <strong>voorbeeld</strong>: pas opmaak en vaste tekst aan in Word, laat de tags tussen <code>{'{{ }}'}</code> staan en upload het.
          </div>
        </div>

        {loading ? (
          <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" />Laden…</div>
        ) : (
          <>
            {frameworks.map(fw => (
              <div key={fw.id} className="card mb-4">
                <div className="card-header fw-semibold">{fwName(fw)}</div>
                <div className="card-body p-0">
                  {KINDS.map((kind, i) => (
                    <KindSection
                      key={kind} kind={kind} first={i === 0}
                      items={templates.filter(t => String(t.framework_id) === String(fw.id) && t.kind === kind)}
                      onExample={() => handleExample(kind, fw)}
                      onUpload={() => openUpload(fw, kind)}
                      onActivate={setActive} onDownload={handleDownload} onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            ))}

            {unlinked.length > 0 && (
              <div className="card mb-4">
                <div className="card-header fw-semibold">Niet aan een framework gekoppeld</div>
                <div className="card-body p-0">
                  <div className="small text-muted px-3 pt-2">
                    Deze sjablonen worden niet gebruikt. Download ze en upload ze opnieuw bij het juiste framework.
                  </div>
                  <TemplateRows items={unlinked} showKind onDownload={handleDownload} onDelete={handleDelete} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function KindSection({ kind, first, items, onExample, onUpload, onActivate, onDownload, onDelete }) {
  const active = items.find(t => t.is_active);
  return (
    <div className={first ? '' : 'border-top'}>
      <div className="d-flex align-items-center flex-wrap gap-2 px-3 pt-3 pb-2">
        <div className="fw-semibold me-auto">{KIND_LABELS[kind]}</div>
        <button className="btn btn-sm btn-outline-secondary" onClick={onExample} title="Voorbeeldsjabloon met alle tags">
          <i className="bi bi-file-earmark-word me-1" />Voorbeeld
        </button>
        <button className="btn btn-sm btn-primary" onClick={onUpload}>
          <i className="bi bi-upload me-1" />Uploaden
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle mb-0">
          <tbody>
            <tr className={active ? '' : 'table-success'}>
              <td style={{ width: 110 }} className="ps-3">
                {active
                  ? <button className="btn btn-sm btn-outline-success py-0" onClick={() => onActivate(active, false)}
                      title="Het ingebouwde document gebruiken">Activeren</button>
                  : <span className="badge bg-success">Actief</span>}
              </td>
              <td colSpan={4} className="text-muted">Ingebouwde lay-out</td>
            </tr>
            {items.map(t => (
              <TemplateRow key={t.id} t={t} onActivate={onActivate} onDownload={onDownload} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplateRow({ t, showKind, onActivate, onDownload, onDelete }) {
  return (
    <tr className={t.is_active ? 'table-success' : ''}>
      {onActivate && (
        <td style={{ width: 110 }} className="ps-3">
          {t.is_active
            ? <span className="badge bg-success">Actief</span>
            : <button className="btn btn-sm btn-outline-success py-0" onClick={() => onActivate(t, true)}>Activeren</button>}
        </td>
      )}
      <td className={`fw-medium ${onActivate ? '' : 'ps-3'}`}>
        {t.name}
        {showKind && <span className="badge bg-light text-muted border ms-2">{KIND_LABELS[t.kind] || KIND_LABELS.report}</span>}
      </td>
      <td className="text-muted small">{t.original_filename}</td>
      <td className="text-muted small">{new Date(t.created_at).toLocaleDateString('nl-BE')} · {t.created_by_name}</td>
      <td className="pe-3">
        <div className="d-flex gap-1 justify-content-end">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => onDownload(t)} title="Downloaden">
            <i className="bi bi-download" />
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(t)} title="Verwijderen">
            <i className="bi bi-trash" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TemplateRows({ items, showKind, onDownload, onDelete }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <tbody>
          {items.map(t => <TemplateRow key={t.id} t={t} showKind={showKind} onDownload={onDownload} onDelete={onDelete} />)}
        </tbody>
      </table>
    </div>
  );
}

function UploadModal({ upload, form, setForm, fileRef, error, uploading, onSubmit, onClose, onExample }) {
  const { fw, kind } = upload;
  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{KIND_LABELS[kind]}-sjabloon uploaden · {fwName(fw)}</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <form id="upload-form" onSubmit={onSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-medium">Naam *</label>
                  <input className="form-control" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    required placeholder={`bijv. ${KIND_LABELS[kind]} FDConsult`} autoFocus />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-medium">Word-bestand (.docx) *</label>
                  <input type="file" className="form-control" accept=".docx" ref={fileRef} required />
                </div>
                <div className="form-check mb-1">
                  <input className="form-check-input" type="checkbox" id="tpl-activate" checked={form.activate}
                    onChange={e => setForm(f => ({ ...f, activate: e.target.checked }))} />
                  <label className="form-check-label small" htmlFor="tpl-activate">Meteen activeren</label>
                </div>
              </form>

              <hr />
              {kind === 'plan' ? (
                <div className="small">
                  <div className="fw-semibold mb-2">📋 Beschikbare placeholders in een auditplan-sjabloon</div>
                  <div className="alert alert-info py-2 small mb-2">
                    Start van het <a href="#voorbeeld" onClick={e => { e.preventDefault(); onExample(); }}>voorbeeldsjabloon</a>: dat bevat alle tags in de opbouw van het standaard auditplan.
                    Zet lus-tags zoals <code>{'{{#days}}'}</code> op een eigen regel; staat de lus in een tabelrij (eerste en laatste cel), dan wordt de rij herhaald.
                  </div>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="text-muted mb-1 fw-medium" style={{ fontSize: 11 }}>PLANGEGEVENS</div>
                      <PlaceholderTable rows={PLAN_PLACEHOLDERS} />
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="text-muted mb-1 fw-medium" style={{ fontSize: 11 }}>LUSSEN EN VOORWAARDEN</div>
                      <PlaceholderTable rows={PLAN_LOOPS} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="small">
                  <div className="fw-semibold mb-2">📋 Beschikbare placeholders in een rapportsjabloon</div>
                  <div className="alert alert-info py-2 small mb-2">
                    Start van het <a href="#voorbeeld" onClick={e => { e.preventDefault(); onExample(); }}>voorbeeldsjabloon</a>: dat bevat alle tags in de opbouw van het standaard rapport.
                  </div>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="text-muted mb-1 fw-medium" style={{ fontSize: 11 }}>AUDIT-GEGEVENS</div>
                      <PlaceholderTable rows={PLACEHOLDERS} />
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="text-muted mb-1 fw-medium" style={{ fontSize: 11 }}>PER BEVINDING (in loop)</div>
                      <div className="alert alert-info py-2 small mb-2">
                        Zet <code>{'{{#findings}}'}</code> en <code>{'{{/findings}}'}</code> rond een tabelrij om die te herhalen per bevinding.
                        Voor per-sectie groepering gebruik <code>{'{{#sections}}'}</code> + <code>{'{{#section_findings}}'}</code>.
                        Cellen met exact een statuslabel krijgen automatisch de statuskleur.
                      </div>
                      <PlaceholderTable rows={FINDING_PLACEHOLDERS} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Annuleren</button>
              <button type="submit" form="upload-form" className="btn btn-primary" disabled={uploading}>
                {uploading
                  ? <><span className="spinner-border spinner-border-sm me-2" />Uploaden…</>
                  : <><i className="bi bi-upload me-1" />Uploaden</>}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}
